import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FaUserShield, FaUserCog, FaEye, FaPlus, FaTimes, FaCopy, FaLock } from 'react-icons/fa';
import saApi, {
  createTeamMember, updateTeamRole, updateTeamStatus, changeMyPassword,
} from '../../services/superAdminApi';
import useSuperAuth from '../../hooks/useSuperAuth';
import useEscapeKey from '../../hooks/useEscapeKey';

// Role is neutral information, not a warning/danger — none of these use the brand red
// (rgba(200,16,46,...) / #c8102e), which this app reserves for alert/danger meaning.
const ROLE_BADGE = {
  owner:    { label: 'Owner',     bg: 'rgba(40,38,31,0.6)',     color: '#ede8e0', Icon: FaUserShield },
  support:  { label: 'Support',   bg: 'rgba(96,165,250,0.12)',  color: '#60a5fa', Icon: FaUserCog   },
  readonly: { label: 'Read-only', bg: 'rgba(107,101,96,0.2)',   color: '#8a847c', Icon: FaEye       },
};

const ROLE_OPTIONS = [
  { value: 'owner',    label: 'Owner',     desc: 'Full control, including team management' },
  { value: 'support',  label: 'Support',   desc: 'Everything except team management' },
  { value: 'readonly', label: 'Read-only', desc: 'View only — no changes' },
];

const MODAL_INPUT = {
  width: '100%', background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)',
  borderRadius: 10, padding: '9px 12px', color: '#ede8e0', fontSize: 13, outline: 'none', colorScheme: 'dark',
};
const MODAL_LABEL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8a847c', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };

// ── Add team member modal ───────────────────────────────────────────────────────
function AddMemberModal({ onClose, onAdded }) {
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [role,     setRole]     = useState('support');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created,  setCreated]  = useState(null); // { name, email, password } — shown once on success

  useEscapeKey(onClose, !submitting);

  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const emailErr   = email.trim().length > 0 && !emailValid;
  const pwValid    = password.length >= 8;
  const pwErr      = password.length > 0 && !pwValid;
  const canSubmit  = !submitting && name.trim() && emailValid && pwValid && ROLE_OPTIONS.some(r => r.value === role);

  const roleDesc = ROLE_OPTIONS.find(r => r.value === role)?.desc;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createTeamMember({ name: name.trim(), email: email.trim(), role, password });
      // Keep the modal open to reveal the credentials once, since there's no email.
      setCreated({ name: name.trim(), email: email.trim(), password });
      toast.success('Team member added');
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add team member');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCreds = () => {
    const text = `TrainAxis SuperAdmin\nEmail: ${created.email}\nTemporary password: ${created.password}`;
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Credentials copied'),
      () => toast.error('Could not copy — copy manually'),
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-md rounded-2xl flex flex-col"
        style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
          <h2 className="font-bold text-[#ede8e0] flex items-center gap-2"><FaPlus className="text-[#c8102e]" /> Add Team Member</h2>
          <button onClick={onClose} className="text-[#8a847c] hover:text-[#ede8e0]"><FaTimes /></button>
        </div>

        {created ? (
          // ── Credentials confirmation (shown once) ──
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm" style={{ color: '#c5bfb5' }}>
              <span className="text-[#ede8e0] font-medium">{created.name}</span> can now sign in. Share these credentials securely — this password won't be shown again.
            </p>
            <div className="rounded-xl p-3 space-y-2" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
              <div className="flex justify-between gap-3 text-xs">
                <span style={{ color: '#8a847c' }}>Email</span>
                <span className="font-medium text-right" style={{ color: '#ede8e0' }}>{created.email}</span>
              </div>
              <div className="flex justify-between gap-3 text-xs">
                <span style={{ color: '#8a847c' }}>Temp password</span>
                <span className="font-mono font-medium text-right" style={{ color: '#ede8e0' }}>{created.password}</span>
              </div>
            </div>
            <button type="button" onClick={copyCreds}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold text-[#c5bfb5] transition-all duration-200"
              style={{ background: '#28261f' }}>
              <FaCopy className="text-xs" /> Copy credentials
            </button>
            <button type="button" onClick={onClose}
              className="w-full py-2.5 rounded-full text-sm font-medium text-white transition-all duration-200 hover:bg-[#e01535]" style={{ background: '#c8102e' }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label style={MODAL_LABEL}>Full Name</label>
                <input style={MODAL_INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <label style={MODAL_LABEL}>Email</label>
                <input style={{ ...MODAL_INPUT, borderColor: emailErr ? '#f87171' : 'rgba(237,232,224,0.08)' }}
                  type="email" value={email} onChange={e => setEmail(e.target.value.replace(/\s/g, ''))}
                  placeholder="jane@trainaxis.com" />
                {emailErr && <p className="text-xs mt-1" style={{ color: '#f87171' }}>Enter a valid email address.</p>}
              </div>
              <div>
                <label style={MODAL_LABEL}>Role</label>
                <select style={MODAL_INPUT} value={role} onChange={e => setRole(e.target.value)}>
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                {roleDesc && <p className="text-xs mt-1.5" style={{ color: '#8a847c' }}>{roleDesc}</p>}
              </div>
              <div>
                <label style={MODAL_LABEL}>Temporary Password</label>
                <input style={{ ...MODAL_INPUT, borderColor: pwErr ? '#f87171' : 'rgba(237,232,224,0.08)' }}
                  type="text" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters" />
                <p className="text-xs mt-1" style={{ color: pwErr ? '#f87171' : '#8a847c' }}>
                  {pwErr
                    ? `${8 - password.length} more character${8 - password.length === 1 ? '' : 's'} needed`
                    : 'Shown as plain text — share it in person or over a secure channel.'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 px-6 py-4 border-t" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 rounded-full text-sm font-medium text-[#c5bfb5] hover:text-[#ede8e0] border transition-colors"
                style={{ borderColor: 'rgba(237,232,224,0.10)' }}>Cancel</button>
              <button type="button" onClick={handleSubmit} disabled={!canSubmit}
                className="flex-1 py-2.5 rounded-full text-sm font-medium text-white transition-all duration-200 hover:bg-[#e01535] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: '#c8102e' }}>
                {submitting ? 'Adding…' : 'Add Member'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Deactivate / reactivate confirm modal ────────────────────────────────────────
function StatusConfirmModal({ member, deactivate, onCancel, onConfirm, busy }) {
  useEscapeKey(onCancel, !busy);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}>
      <div className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }}
        onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-[#ede8e0] mb-2">{deactivate ? 'Deactivate' : 'Reactivate'} member?</h3>
        <p className="text-sm mb-5" style={{ color: '#c5bfb5' }}>
          {deactivate ? (
            <><span className="text-[#ede8e0] font-medium">{member.name}</span> will be signed out and blocked from the platform immediately.</>
          ) : (
            <><span className="text-[#ede8e0] font-medium">{member.name}</span> will regain access with their existing role.</>
          )}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={() => !busy && onCancel()}
            className="flex-1 py-2.5 rounded-full text-sm font-medium text-[#c5bfb5] hover:text-[#ede8e0] border transition-colors"
            style={{ borderColor: 'rgba(237,232,224,0.10)' }}>Cancel</button>
          <button type="button" onClick={onConfirm} disabled={busy}
            className="flex-1 py-2.5 rounded-full text-sm font-medium text-white transition-all duration-200 disabled:opacity-50"
            style={{ background: deactivate ? '#c8102e' : '#059669' }}>
            {busy ? 'Working…' : (deactivate ? 'Deactivate' : 'Reactivate')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Your-account security card ───────────────────────────────────────────────────
function AccountSecurityCard() {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy,    setBusy]    = useState(false);

  const checks = [
    { ok: next.length >= 8,       label: '8+ characters' },
    { ok: /[a-zA-Z]/.test(next),  label: 'A letter' },
    { ok: /\d/.test(next),        label: 'A number' },
    { ok: next.length > 0 && next === confirm, label: 'Passwords match' },
  ];
  const nextValid = next.length >= 8 && /[a-zA-Z]/.test(next) && /\d/.test(next);
  const canSubmit = !busy && current && nextValid && next === confirm;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await changeMyPassword({ currentPassword: current, newPassword: next });
      toast.success('Password changed');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border p-5 max-w-md" style={{ borderColor: 'rgba(237,232,224,0.08)', background: '#1e1d1b' }}>
      <h2 className="font-bold text-[#ede8e0] flex items-center gap-2 mb-1">
        <FaLock className="text-xs" style={{ color: '#8a847c' }} /> Your Account
      </h2>
      <p className="text-xs mb-4" style={{ color: '#8a847c' }}>Change the password for your own superadmin account.</p>

      <div className="space-y-3">
        <div>
          <label style={MODAL_LABEL}>Current Password</label>
          <input style={MODAL_INPUT} type="password" value={current} autoComplete="current-password"
            onChange={e => setCurrent(e.target.value)} placeholder="Current password" />
        </div>
        <div>
          <label style={MODAL_LABEL}>New Password</label>
          <input style={MODAL_INPUT} type="password" value={next} autoComplete="new-password"
            onChange={e => setNext(e.target.value)} placeholder="New password" />
        </div>
        <div>
          <label style={MODAL_LABEL}>Confirm New Password</label>
          <input style={{ ...MODAL_INPUT, borderColor: confirm && confirm !== next ? '#f87171' : 'rgba(237,232,224,0.08)' }}
            type="password" value={confirm} autoComplete="new-password"
            onChange={e => setConfirm(e.target.value)} placeholder="Re-enter new password" />
        </div>

        {(next || confirm) && (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {checks.map(c => (
              <span key={c.label} className="text-xs" style={{ color: c.ok ? '#34d399' : '#6b6560' }}>
                {c.ok ? '✓' : '○'} {c.label}
              </span>
            ))}
          </div>
        )}

        <button type="button" onClick={handleSubmit} disabled={!canSubmit}
          className="w-full py-2.5 rounded-full text-sm font-medium text-white transition-all duration-200 hover:bg-[#e01535] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: '#c8102e' }}>
          {busy ? 'Saving…' : 'Change Password'}
        </button>
      </div>
    </div>
  );
}

export default function Team() {
  const { superAdmin } = useSuperAuth();
  const qc      = useQueryClient();
  const isOwner = superAdmin?.role === 'owner';

  const [showAdd,   setShowAdd]   = useState(false);
  const [statusFor, setStatusFor] = useState(null); // member awaiting status confirmation

  const { data, isPending } = useQuery({
    queryKey: ['sa-admins'],
    queryFn:  () => saApi.get('/api/super-admin/admins').then(r => r.data),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-admins'] });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }) => updateTeamRole(id, role),
    onSuccess:  () => { toast.success('Role updated'); refresh(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update role'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateTeamStatus(id, isActive),
    onSuccess:  (_res, vars) => { toast.success(vars.isActive ? 'Member reactivated' : 'Member deactivated'); setStatusFor(null); refresh(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update status'),
  });

  const admins  = data?.admins || [];
  const colSpan = isOwner ? 5 : 4;

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#ede8e0]">Platform Team</h1>
          <p className="text-sm mt-1" style={{ color: '#8a847c' }}>
            Super administrators with platform-level access. All actions are logged.
          </p>
        </div>
        {isOwner && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium text-white transition-all duration-200 hover:bg-[#e01535] flex-shrink-0"
            style={{ background: '#c8102e' }}>
            <FaPlus /> Add member
          </button>
        )}
      </div>

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
              {['Member', 'Role', 'Last Login', 'Status', ...(isOwner ? ['Actions'] : [])].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                  style={{ color: '#6b6560' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              Array.from({ length: 2 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                  {[1,2,3,4].map(j => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-4 rounded animate-pulse" style={{ background: '#28261f', width: j === 1 ? '140px' : '80px' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : !admins.length ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center" style={{ color: '#6b6560' }}>
                  No team members yet
                </td>
              </tr>
            ) : admins.map(a => {
              const badge  = ROLE_BADGE[a.role] || ROLE_BADGE.readonly;
              const isSelf = a._id === superAdmin?.id;
              const inactive = !a.isActive;
              return (
                <tr key={a._id}
                  style={{ borderBottom: '1px solid rgba(237,232,224,0.05)', opacity: inactive ? 0.55 : 1 }}
                  className="hover:bg-[#28261f] transition-colors">

                  {/* Member */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                        style={{ background: inactive ? '#28261f' : '#c8102e', color: '#fff' }}>
                        {a.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-[#ede8e0] flex items-center gap-2">
                          {a.name}
                          {isSelf && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#28261f', color: '#8a847c' }}>you</span>}
                        </div>
                        <div className="text-xs truncate" style={{ color: '#8a847c' }}>{a.email}</div>
                      </div>
                    </div>
                  </td>

                  {/* Role */}
                  <td className="px-4 py-4">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </td>

                  {/* Last login */}
                  <td className="px-4 py-4 text-xs" style={{ color: '#8a847c' }}>
                    {a.lastLoginAt ? new Date(a.lastLoginAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{
                        background: a.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                        color:      a.isActive ? '#34d399'               : '#f87171',
                      }}>
                      {a.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Actions (owner only, never on your own row) */}
                  {isOwner && (
                    <td className="px-4 py-4">
                      {!isSelf ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={a.role}
                            onChange={e => roleMutation.mutate({ id: a._id, role: e.target.value })}
                            className="text-xs rounded-full px-2.5 py-1.5 outline-none cursor-pointer transition-colors"
                            style={{ background: '#28261f', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)', colorScheme: 'dark' }}
                          >
                            <option value="owner">Owner</option>
                            <option value="support">Support</option>
                            <option value="readonly">Read-only</option>
                          </select>
                          <button
                            onClick={() => setStatusFor(a)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors"
                            style={a.isActive
                              ? { background: 'rgba(255,69,58,0.08)', border: '1px solid rgba(255,69,58,0.2)', color: '#ff453a' }
                              : { background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399' }}>
                            {a.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs" style={{ color: '#6b6560' }}>—</span>
                      )}
                    </td>
                  )}

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs" style={{ color: '#6b6560' }}>
        Role changes take effect immediately. Only the Owner can add members or modify roles.
      </p>

      {/* Your-account security (everyone) */}
      <AccountSecurityCard />

      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onAdded={refresh}
        />
      )}

      {statusFor && (
        <StatusConfirmModal
          member={statusFor}
          deactivate={statusFor.isActive}
          busy={statusMutation.isPending}
          onCancel={() => setStatusFor(null)}
          onConfirm={() => statusMutation.mutate({ id: statusFor._id, isActive: !statusFor.isActive })}
        />
      )}
    </div>
  );
}
